import Swiper from 'swiper'
import { EffectCreative, Keyboard, Navigation } from 'swiper/modules'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { numberAttr, optional, prefersReducedMotion, qsa } from '@/lib/dom'

const TILT_MAX = 5 // deg
const CHAPTER_LABELS = ['原点', '叩き上げ', '経営者として']

/**
 * MY ROOTS セクション。
 *
 * iPhone モックの画面が Swiper になっていて、
 *   スワイプ / ドラッグ / 画面タップ（左右半分）/ 矢印ボタン / 矢印キー / 年表レール
 * のどれでもページが変わる。
 *
 * ページャは Swiper の Pagination を使わず、レール・セグメント・カウンタ・
 * 背後の年号を slideChange でまとめて更新している（既存 CSS との衝突回避）。
 *
 * 端末はポインタに追従して軽く傾き、ガラスの反射がそれに合わせて動く。
 * ドラッグ中と reduced-motion では傾けない。
 */
export function initRoots(): void {
  const container = optional('.js-roots-swiper')
  if (!container) return

  const section = container.closest<HTMLElement>('.section-roots')
  const stage = optional('.js-roots-stage')
  const tilt = optional('.js-roots-tilt')
  const glare = optional('.js-roots-glare')
  const rail = qsa<HTMLButtonElement>('.c-roots-rail-item')
  const segs = qsa<HTMLElement>('.c-iphone-seg')
  const ghosts = qsa<HTMLElement>('.c-roots-ghost-item')
  const counter = optional('.js-roots-current')
  const live = optional('.c-roots-live')
  const prev = optional<HTMLButtonElement>('.js-roots-prev')
  const next = optional<HTMLButtonElement>('.js-roots-next')
  const slides = qsa<HTMLElement>('.c-roots-slide', container)
  const reads = qsa<HTMLElement>('.c-read')
  const reduced = prefersReducedMotion()

  const railBox = optional('.p-roots-rail')

  /**
   * タブレット以下ではレールが横スクロールになるので、現在地を見える位置へ寄せる。
   * scrollIntoView はページ全体を縦に動かしてしまうため、scrollLeft を直接指定する。
   */
  const revealInRail = (button: HTMLElement): void => {
    if (!railBox || railBox.scrollWidth <= railBox.clientWidth) return
    const target = button.offsetLeft + button.offsetWidth / 2 - railBox.clientWidth / 2
    railBox.scrollTo({
      left: Math.max(0, Math.min(target, railBox.scrollWidth - railBox.clientWidth)),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })
  }

  /** レール・セグメント・カウンタ・年号・時代カラーを一度に合わせる */
  const sync = (index: number): void => {
    rail.forEach((button, i) => {
      const current = i === index
      button.classList.toggle('is-current', current)
      button.classList.toggle('is-past', i < index)
      // aria-selected は tab などのロール専用で、素の button には不正（a11y 監査で落ちる）
      button.setAttribute('aria-pressed', String(current))
      if (current) revealInRail(button)
    })

    segs.forEach((seg, i) => {
      seg.classList.toggle('is-current', i === index)
      seg.classList.toggle('is-past', i < index)
    })

    ghosts.forEach((ghost, i) => ghost.classList.toggle('is-current', i === index))

    // 端末の外に置いた本文も入れ替える
    reads.forEach((r, i) => r.classList.toggle('is-current', i === index))

    if (counter) counter.textContent = String(index + 1).padStart(2, '0')

    // 時代（CHAPTER）が変わったらセクション全体のアクセントカラーを差し替える
    const era = slides[index]?.dataset['era'] ?? '0'
    section?.setAttribute('data-era', era)

    if (live) {
      const label = CHAPTER_LABELS[Number(era)] ?? ''
      live.textContent = `${index + 1} / ${slides.length}　${label}`
    }
  }

  /** スライドの中身を下から立ち上げる */
  const enter = (index: number): void => {
    if (reduced) return
    const inner = slides[index]?.querySelector('.c-roots-slide-inner')
    if (!inner) return
    inner.scrollTop = 0
    gsap.fromTo(
      inner.children,
      { y: 14, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', stagger: 0.055, overwrite: true },
    )
  }

  const swiper = new Swiper(container, {
    modules: reduced ? [Navigation, Keyboard] : [Navigation, Keyboard, EffectCreative],
    slidesPerView: 1,
    speed: reduced ? 0 : 520,
    grabCursor: true,
    keyboard: { enabled: true, onlyInViewport: true },
    resistanceRatio: 0.55,
    // 前のページは奥へ引き込み、次のページは横から差し込む
    ...(reduced
      ? {}
      : {
          effect: 'creative' as const,
          creativeEffect: {
            prev: { translate: ['-18%', 0, -220], scale: 0.92, opacity: 0 },
            next: { translate: ['100%', 0, 0] },
            limitProgress: 2,
          },
        }),
    ...(prev && next ? { navigation: { prevEl: prev, nextEl: next } } : {}),
    on: {
      slideChange: (instance) => sync(instance.activeIndex),
      slideChangeTransitionStart: (instance) => enter(instance.activeIndex),
      // 画面の左右どちらを叩いたかで前後に送る（ストーリーUI と同じ操作感）
      tap: (instance, event) => {
        // Swiper 11 は pointer イベントに正規化されるが、念のため clientX の有無で判定する
        const clientX =
          'clientX' in event
            ? event.clientX
            : (event as unknown as TouchEvent).changedTouches?.[0]?.clientX
        if (typeof clientX !== 'number') return

        const rect = instance.el.getBoundingClientRect()
        const x = clientX - rect.left
        if (x < rect.width * 0.34) instance.slidePrev()
        else if (x > rect.width * 0.66) instance.slideNext()
      },
    },
  })

  rail.forEach((button) => {
    button.addEventListener('click', () => {
      swiper.slideTo(numberAttr(button, 'data-roots-index', 0))
    })
  })

  sync(swiper.activeIndex)
  enter(swiper.activeIndex)

  /* ---- 端末の傾き ---- */
  if (tilt && stage && !reduced && window.matchMedia('(hover: hover)').matches) {
    const rotX = gsap.quickTo(tilt, 'rotationX', { duration: 0.6, ease: 'power3.out' })
    const rotY = gsap.quickTo(tilt, 'rotationY', { duration: 0.6, ease: 'power3.out' })
    const glareX = glare ? gsap.quickTo(glare, 'xPercent', { duration: 0.6, ease: 'power3.out' }) : null
    let dragging = false

    stage.addEventListener('pointermove', (event) => {
      if (dragging) return
      const rect = stage.getBoundingClientRect()
      const nx = (event.clientX - rect.left) / rect.width - 0.5 // -0.5 .. 0.5
      const ny = (event.clientY - rect.top) / rect.height - 0.5
      rotY(nx * TILT_MAX * 2)
      rotX(-ny * TILT_MAX * 2)
      glareX?.(nx * 26)
    })

    const rest = (): void => {
      rotX(0)
      rotY(0)
      glareX?.(0)
    }

    stage.addEventListener('pointerleave', rest)
    // ドラッグ中は傾きを固定して、スワイプ操作と喧嘩させない
    container.addEventListener('pointerdown', () => {
      dragging = true
      rest()
    })
    window.addEventListener('pointerup', () => {
      dragging = false
    })
  }

  /* ---- セクション入場時に端末をせり上げる ---- */
  if (stage && !reduced) {
    gsap.from(stage, {
      y: 60,
      opacity: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: { trigger: stage, start: 'top 82%', once: true },
    })
  }

  // section04 の装飾円（旧 HISTORY から引き継ぎ）
  if (!reduced) {
    qsa<HTMLElement>('.js-section-history-circle-bg').forEach((circle) => {
      gsap.fromTo(
        circle,
        { scale: 1 },
        {
          scale: 1.3,
          ease: 'none',
          scrollTrigger: {
            trigger: circle.closest('section') ?? circle,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true,
          },
        },
      )
    })
  }

  ScrollTrigger.refresh()
}
